import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { replaceHelpPageTermsInText } from 'src/modules/support/helpArticles/functions/helpPageTerms';
import { HelpCenterCategory } from '../models/helpCenterCategory';
import { getHelpCenterCategories } from '../services/helpCenterService';
import { groupHelpCenterCategories } from '../functions/groupHelpCenterCategories';
import { HelpSubCategoryCard } from '../components/helpSubCategoryCard';
import { HelpSearchField } from '../components/helpSearchField';
import { HelpCenterFrame } from '../components/helpCenterFrame';

export const HelpPage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();

  const [categories, setCategories] = useState<HelpCenterCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);

  // Backoffice kräver samma behörighet för att läsa, så bara de som kan använda det ser länken.
  const canEditHelpArticles = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  // Felet visas av interceptorn. Då visas inte texten om att det saknas artiklar, eftersom den inte stämmer.
  useEffect(() => {
    const load = async () => {
      try {
        const result = await getHelpCenterCategories();
        if (isMounted()) setCategories(result.data);
      } catch (e) {
        ConsoleHelper.log(e);
        if (isMounted()) setFailed(true);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };
    load();
  }, [isMounted]);

  const sections = useMemo(() => groupHelpCenterCategories(categories), [categories]);

  return (
    <HelpCenterFrame>
      <GridWrapper>
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <MainContentHeader title={t('common:helpLinkText')} />
          {canEditHelpArticles && (
            <Button
              component={Link}
              to={RoutesEnum.HelpArticleCategories}
              variant='outlined'
              size='small'
              startIcon={<AdminPanelSettingsOutlined fontSize='small' />}
            >
              {t('help:adminLink')}
            </Button>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <HelpSearchField />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ position: 'relative', minHeight: loading ? '10rem' : undefined }}>
            <LoadingOverlay loading={loading} />
            {!loading && !failed && sections.length === 0 && (
              <Typography color='text.secondary' sx={{ mt: 4 }}>
                {t('help:empty')}
              </Typography>
            )}
            {sections.map(({ mainCategory, cards }) => (
              <Box sx={{ mt: 4 }} key={mainCategory.id}>
                <MainContentHeader title={mainCategory.name} />
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  {cards.map((category) => (
                    <Grid item xs={12} sm={6} md={4} key={category.id}>
                      <HelpSubCategoryCard
                        title={category.name}
                        subtitle={
                          category.description ? replaceHelpPageTermsInText(category.description, currentUser?.mainSettings ?? []) : undefined
                        }
                        articleCount={category.articleCount}
                        to={`${RoutesEnum.HelpCategory}/${category.id}`}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </Grid>
      </GridWrapper>
    </HelpCenterFrame>
  );
};
